import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Button, Upload, useUploadFile, Icon } from '@saviynt/design-system';
import { TITLES, DESCRIPTIONS, BUTTON_LABELS } from '../../constants/stringConstants';
import useStyles from './styles/DisconnectedDiscoveryModal.styles';

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ZIP_MAGIC_BYTES = [0x50, 0x4b, 0x03, 0x04];
const FILE_NAME_REGEX = /^[a-zA-Z0-9_\-. ]+$/;

const VALIDATION_ERRORS = {
  EMPTY_FILE: 'File is empty (0 bytes). Please upload a valid .zip file.',
  INVALID_TYPE: 'Invalid file type. Only .zip files are allowed.',
  FILE_TOO_LARGE: `File size exceeds the maximum limit of ${MAX_FILE_SIZE_MB}MB.`,
  INVALID_ZIP: 'File appears to be corrupted or is not a valid .zip archive.',
  INVALID_NAME:
    'File name contains invalid characters. Use only letters, numbers, spaces, hyphens, underscores, and dots.',
};

const validateFileType = (file) => {
  const extension = file.fileName?.split('.').pop()?.toLowerCase();
  return extension === 'zip';
};

const validateFileSize = (file) => file.fileSize > 0 && file.fileSize <= MAX_FILE_SIZE_BYTES;

const validateFileName = (file) => FILE_NAME_REGEX.test(file.fileName);

const validateZipSignature = (file) =>
  new Promise((resolve) => {
    if (!file.file) {
      resolve(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const isValid = ZIP_MAGIC_BYTES.every((byte, i) => arr[i] === byte);
      resolve(isValid);
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.file.slice(0, 4));
  });

function DisconnectedDiscoveryModal({ isOpen, onClose, onSubmit }) {
  const classes = useStyles();
  const [isClosing, setIsClosing] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [validating, setValidating] = useState(false);
  const {
    uploadedFiles,
    handleFileSelect,
    handleFileDelete,
    isMaxNumberUploaded,
    isMaxTotalSizeReached,
  } = useUploadFile(MAX_FILE_SIZE_MB, 1, MAX_FILE_SIZE_MB);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setValidationError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      setValidationError(null);
      return;
    }

    const file = uploadedFiles[0];

    if (file.isError) {
      setValidationError(null);
      return;
    }

    if (file.fileSize === 0) {
      setValidationError(VALIDATION_ERRORS.EMPTY_FILE);
      return;
    }

    if (!validateFileType(file)) {
      setValidationError(VALIDATION_ERRORS.INVALID_TYPE);
      return;
    }

    if (!validateFileSize(file)) {
      setValidationError(
        file.fileSize > MAX_FILE_SIZE_BYTES
          ? VALIDATION_ERRORS.FILE_TOO_LARGE
          : VALIDATION_ERRORS.EMPTY_FILE
      );
      return;
    }

    if (!validateFileName(file)) {
      setValidationError(VALIDATION_ERRORS.INVALID_NAME);
      return;
    }

    setValidating(true);
    validateZipSignature(file).then((isValid) => {
      setValidationError(isValid ? null : VALIDATION_ERRORS.INVALID_ZIP);
      setValidating(false);
    });
  }, [uploadedFiles]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  const handleSubmit = () => {
    if (uploadedFiles.length > 0 && !validationError && !validating) {
      onSubmit(uploadedFiles);
    }
  };

  const isSubmitDisabled =
    uploadedFiles.length === 0 || !!validationError || validating || uploadedFiles[0]?.isError;

  if (!isOpen && !isClosing) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={`${classes.overlay} ${isClosing ? classes.overlayClosing : ''}`}
      onClick={handleClose}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={`${classes.modal} ${isClosing ? classes.modalClosing : ''}`}
        onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title}>{TITLES.DISCONNECTED_DISCOVERY}</Typography>
            <Typography className={classes.subtitle}>
              {DESCRIPTIONS.DISCONNECTED_DISCOVERY_MODAL}
            </Typography>
          </div>
          <Button kind="ghost" size="small" onClick={handleClose}>
            <Icon kind="Close" size="smallMedium" color="neutral-100" label="Close" />
          </Button>
        </div>
        <div className={classes.body}>
          <Upload
            uploadedFiles={uploadedFiles}
            onFileSelect={handleFileSelect}
            onFileDelete={handleFileDelete}
            acceptTypes=".zip"
            isMaxNumberUploaded={isMaxNumberUploaded}
            isMaxTotalSizeReached={isMaxTotalSizeReached}
            kind="dropzone"
            buttonLabel={BUTTON_LABELS.BROWSE}
            dropzoneLinkText="browse"
            fileTypesAllowedText={`${DESCRIPTIONS.ZIP_FILES_ONLY} (Max ${MAX_FILE_SIZE_MB}MB)`}
            maxFileSize={MAX_FILE_SIZE_MB}
          />
          {validationError && (
            <div className={classes.validationError}>
              <Icon
                kind="AlertCriticalFilledSeparateColors"
                size="small"
                color="--color-foreground-critical-medium"
                label="Error"
              />
              <Typography className={classes.validationErrorText}>{validationError}</Typography>
            </div>
          )}
        </div>
        <div className={classes.footer}>
          <Button kind="outlined" size="medium" onClick={handleClose}>
            {BUTTON_LABELS.CANCEL}
          </Button>
          <Button kind="filled" size="medium" isDisabled={isSubmitDisabled} onClick={handleSubmit}>
            {BUTTON_LABELS.SUBMIT}
          </Button>
        </div>
      </div>
    </div>
  );
}

DisconnectedDiscoveryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default DisconnectedDiscoveryModal;
